import { Moment } from 'moment';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User } from 'api/models';
import { TimesheetApprovalType } from 'api/timesheets/configuration/configuration';
import {
  TimeBarCell,
  TotalTimeCell,
} from 'components/organisms/timesheets/common/cells/time-cells';
import { FooterRow } from 'components/organisms/timesheets/common/timesheet-table';
import {
  getDays,
  isNonWorkingDay,
} from 'components/organisms/timesheets/common/utils';
import {
  Minutes,
  WorkingDaysConfiguration,
} from 'components/organisms/user-capacity';
import { fullName } from 'utils/full-name';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import { FormatTime } from '../common/format-time';
import { TimesheetWeekSummary } from '../types';

const Value = styled.div`
  width: 100%;
  height: 100%;
`;

const Footer = styled(FooterRow)`
  .ant-table-thead > tr > th {
    background: var(--coolest-gray-20);
    font-weight: normal;
    padding: 0;
  }
  .ant-table-thead > tr > th:first-child {
    padding-left: var(--spacing-05);
  }
`;

const TimeBar = styled(TimeBarCell)`
  height: unset;
  padding: calc(calc(1 * var(--spacing-03)) - 1px) var(--spacing-04);

  div {
    height: unset;
  }
  &:after {
    padding: calc(calc(1 * var(--spacing-03)) - 1px) var(--spacing-04);
  }
`;
const TotalTime = styled(TotalTimeCell)`
  height: unset;
  padding: calc(calc(1 * var(--spacing-03)) - 1px) var(--spacing-04);
`;

export const SummaryRow: FC<{
  startDate: Moment;
  capacityPerDayInMinutes: Minutes;
  timeSheetSummary: TimesheetWeekSummary;
  workingDays: WorkingDaysConfiguration;
  user: User;
  approvalSetting;
}> = ({
  startDate,
  timeSheetSummary,
  capacityPerDayInMinutes,
  workingDays,
  user,
  approvalSetting,
}) => {
  const isMangerApproal = useMemo(
    () => approvalSetting === TimesheetApprovalType.Manager,
    [approvalSetting],
  );
  const days = getDays(startDate).map((day) => {
    const isTimeOff = checkIfCurrentDateHasTimeOff(
      user?.userTimeOffDetails,
      day,
    );

    return {
      title: isMangerApproal ? (
        <TimeBar
          minutes={
            timeSheetSummary.totalMinutesForDays[day.format('YYYY-MM-DD')] ?? 0
          }
          capacity={
            !isNonWorkingDay(day, workingDays) ? capacityPerDayInMinutes : 0
          }
          showBorder={false}
          isTimeOff={isTimeOff}
          barColor="var(--green-20)"
          overCapacityColor="var(--red-30)"
          userName={fullName(user)}
        />
      ) : (
        <Value>
          <FormatTime
            value={
              timeSheetSummary.totalMinutesForDays[day.format('YYYY-MM-DD')] ??
              0
            }
            showHypenIfZero={false}
          />
        </Value>
      ),
      width: '8%',
    };
  });

  const columns = [
    {
      title: (
        <strong>
          <FormattedMessage id="timesheets.column.total_time" />
        </strong>
      ),
      width: isMangerApproal ? '32%' : '20%',
    },
    ...days,
    {
      title: <TotalTime minutes={timeSheetSummary.totalMinutesForWeek} />,
      width: '8%',
    },
    {
      width: isMangerApproal ? '4%' : '6%',
      title: '',
    },
    isMangerApproal
      ? {
          width: '10%',
          title: '',
        }
      : {},
  ];

  return <Footer columns={columns} dataSource={[]} pagination={false} />;
};
